import * as React from "react";
import {
  Card,
  CardHeader,
  Text,
  Button,
  Checkbox,
  makeStyles,
  tokens,
  Spinner,
} from "@fluentui/react-components";
import { BookRegular, AddCircle20Regular } from "@fluentui/react-icons";
import { Paper } from "../../types/paper";

interface PaperListProps {
  papers: Paper[];
  selectedPaperIds?: Set<string>;
  onToggleSelection?: (paperId: string) => void;
  isLoading?: boolean;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#f5f7fa",
    padding: "8px 0",
  },
  paperCard: {
    margin: "8px 12px",
    padding: "14px",
    backgroundColor: "#ffffff",
    border: "2px solid #e3e8ef",
    borderRadius: "10px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
  },
  paperCardSelected: {
    margin: "8px 12px",
    padding: "14px",
    backgroundColor: "#f0f4ff",
    border: "2px solid #667eea",
    borderRadius: "10px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
  },
  paperCardHover: {
    margin: "8px 12px",
    padding: "14px",
    backgroundColor: "#ffffff",
    border: "2px solid #667eea",
    borderRadius: "10px",
    boxShadow: "0 8px 24px rgba(102, 126, 234, 0.15)",
    transform: "translateY(-2px)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
  },
  paperHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  checkboxContainer: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
  },
  checkboxWrapper: {
    marginTop: "1px",
    flexShrink: 0,
  },
  contentWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    minWidth: 0,
  },
  titleContainer: {
    display: "flex",
    alignItems: "flex-start",
    gap: "4px",
    minWidth: 0,
  },
  paperTitle: {
    fontWeight: "600",
    fontSize: "13px",
    lineHeight: "1.4",
    color: "#1a202c",
    display: "-webkit-box",
    WebkitLineClamp: "3",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    flex: 1,
    minWidth: 0,
    wordBreak: "break-word",
  },
  starIcon: {
    fontSize: "14px",
    flexShrink: 0,
    marginTop: "1px",
  },
  paperAuthors: {
    fontSize: "11px",
    color: "#4a5568",
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    fontWeight: "500",
    lineHeight: "1.3",
    wordBreak: "break-word",
  },
  paperMeta: {
    fontSize: "10px",
    color: "#718096",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap",
  },
  ratingsContainer: {
    display: "flex",
    gap: "6px",
    marginTop: "6px",
    flexWrap: "wrap",
  },
  ratingBadge: {
    fontSize: "9px",
    fontWeight: "600",
    padding: "2px 6px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "3px",
    whiteSpace: "nowrap",
  },
  insertButton: {
    width: "100%",
    marginTop: "14px",
    borderRadius: "8px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    border: "none",
    fontWeight: "600",
    fontSize: "13px",
    transition: "all 0.2s ease",
    boxShadow: "0 2px 8px rgba(102, 126, 234, 0.25)",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.35)",
      transform: "translateY(-1px)",
    },
  },
  icon: {
    color: "#667eea",
    fontSize: "20px",
  },
  emptyState: {
    padding: "60px 20px",
    textAlign: "center",
    color: "#718096",
    fontSize: "14px",
    backgroundColor: "#f5f7fa",
  },
  loadingState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "60px 20px",
    gap: "16px",
    backgroundColor: "#f5f7fa",
  },
});

const PaperList: React.FC<PaperListProps> = ({ 
  papers, 
  selectedPaperIds = new Set(),
  onToggleSelection,
  isLoading = false 
}) => {
  const styles = useStyles();
  const [hoveredCard, setHoveredCard] = React.useState<string | null>(null);

  const isSelected = (paperId: string) => selectedPaperIds.has(paperId);

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner size="large" />
        <Text style={{ color: "#999999", fontSize: "13px" }}>Loading papers...</Text>
      </div>
    );
  }

  if (papers.length === 0) {
    return (
      <div className={styles.emptyState}>
        No papers found. Try adjusting your filters.
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {papers.map((paper) => {
        const selected = isSelected(paper.id);
        const cardClass = selected 
          ? styles.paperCardSelected 
          : hoveredCard === paper.id 
            ? styles.paperCardHover 
            : styles.paperCard;
        
        return (
          <Card 
            key={paper.id} 
            className={cardClass}
            onMouseEnter={() => setHoveredCard(paper.id)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <CardHeader
              image={<BookRegular className={styles.icon} />}
              header={
                <div className={styles.paperHeader}>
                  <div className={styles.checkboxContainer}>
                    {onToggleSelection && (
                      <div className={styles.checkboxWrapper}>
                        <Checkbox
                          checked={selected}
                          onChange={() => onToggleSelection(paper.id)}
                          style={{ 
                            cursor: "pointer",
                          }}
                        />
                      </div>
                    )}
                    <div className={styles.contentWrapper}>
                      <div className={styles.titleContainer}>
                        <Text className={styles.paperTitle}>{paper.title}</Text>
                        {paper.starred && <span className={styles.starIcon}>⭐</span>}
                      </div>
                      <Text className={styles.paperAuthors}>
                        {paper.authors.join(", ")}
                      </Text>
                      {(paper.year || paper.journal) && (
                        <Text className={styles.paperMeta}>
                          {paper.year}{paper.journal && ` • ${paper.journal}`}
                        </Text>
                      )}
                      {(() => {
                        const ratings = [paper.clarity, paper.usefulness, paper.impact].filter((r) => r !== undefined);
                        if (ratings.length === 0) return null;
                        const average = (ratings.reduce((sum, r) => sum + r, 0) / ratings.length).toFixed(1);
                        return (
                          <div className={styles.ratingsContainer}>
                            <span className={styles.ratingBadge} style={{ backgroundColor: "#ede9fe", color: "#6b21a8" }}>
                              ⭐ Average Rating: {average}/5
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              }
            />
          </Card>
        );
      })}
    </div>
  );
};

export default React.memo(PaperList, (prevProps, nextProps) => {
  // Only re-render if papers, selectedPaperIds, or isLoading actually changed
  return (
    prevProps.papers === nextProps.papers &&
    prevProps.selectedPaperIds === nextProps.selectedPaperIds &&
    prevProps.isLoading === nextProps.isLoading
  );
});
